import { LEMONSQUEEZY_PLAN_INTERVALS } from 'src/types/lemonsqueezyTypes';
import { PricingPlanApiResponse } from 'src/types/plan';
import { DOMAIN_NAME_EXTRACTOR_FROM_EMAIL } from './Regex';

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]; // Same as Notion DBs

export const WORDS_PER_ROW_CELL = 100;

export const EMPTY_STRING = '';
export const ZERO = 0;
export const ONE = 1;

export const NOT_AVAILABLE = 'NA';

export const truncateString = (
  str: string | undefined,
  num: number,
): string => {
  if (!str) return NOT_AVAILABLE;

  if (str.length <= num) {
    return str;
  }

  return str.slice(0, num).trim() + '...';
};

export const extractGoogleUserId = (userId: string) => {
  const parts = userId.split('/');

  return parts.length > 1 ? parts[1] : null;
};

export const getDiscountPercentage = (
  basePrice: number,
  discountedPrice: number,
): number => {
  if (!basePrice || !discountedPrice) return 0;

  const discountPercentage = ((basePrice - discountedPrice) / basePrice) * 100;

  return Math.round(discountPercentage);
};

export const calculateMaxDiscount = (
  pricingDetails: PricingPlanApiResponse[],
  timeInterval: typeof LEMONSQUEEZY_PLAN_INTERVALS,
): number => {
  return Math.max(
    ...pricingDetails.map((plan) => {
      const monthlyPriceDetails = plan.prices?.find(
        ({ interval: i }) => i === timeInterval.month,
      );
      const yearlyPriceDetails = plan.prices?.find(
        ({ interval: i }) => i === timeInterval.year,
      );

      const monthlyPrice = Number(monthlyPriceDetails?.price) || 0;
      const yearlyPrice = Number(yearlyPriceDetails?.price) || 0;
      const monthlyEquivalentOfYearlyPrice = yearlyPrice / 12;

      return getDiscountPercentage(
        monthlyPrice,
        monthlyEquivalentOfYearlyPrice,
      );
    }),
  );
};

export function minifyNumber(value: number | string) {
  // Use the toLocaleString method to add suffixes to the number
  return value.toLocaleString('en-US', {
    // add suffixes for thousands, millions, and billions
    // the maximum number of decimal places to use
    maximumFractionDigits: 2, // e.g. 1567 -> 1.57K
    // specify the abbreviations to use for the suffixes
    notation: 'compact',
    compactDisplay: 'short',
  });
}

/**
 * Google Workspace differentiates organizations based on their domain names, which are unique identifiers on the internet. When an organization signs up for Google Workspace, they must provide a domain name that they own, such as hanabitech.com. This domain name is used to create email addresses for the users within that organization.
 * If another organization owns the hanabitech.in domain and signs up for Google Workspace, they will have their own separate account and user management system. Google treats these as entirely separate entities, even if the second-level domain (SLD) is the same (in this case, hanabitech).
 * Here's how Google differentiates the users from the two organizations:
 * - A user from hanabitech.com might have an email address like santhosh.kumar@hanabitech.com
 * - A user from hanabitech.in might have an email address like santhosh.kumar@hanabitech.in.
 * Each organization manages its own Google Workspace account, and there is no overlap or sharing of user information between the two. They are treated as distinct entities with their own set of users, groups, and permissions. The domain name serves as the primary identifier for the organization within Google's systems, ensuring that each organization's data and user accounts remain separate and secure.
 */
export function getDomainNameByEmail(email: string): string | null {
  /**
   * Collection name cannot be simply the Second Level Domain name without the Top Level Domain name, they both need to be considered.
   * When differentiating between Google Workspace organizations, the domain name, including the Top-Level Domain (TLD),
   * is crucial. Different TLDs can represent different entities or organizations.
   * For example, hanabitech.com and hanabitech.in could potentially belong to different organizations.
   */

  const match = DOMAIN_NAME_EXTRACTOR_FROM_EMAIL.exec(email);

  if (!match || match.length < 2 || !match[1]) {
    return null;
  }

  return match[1];
}

export function isHanabitechUser(email: string): boolean {
  return email.endsWith('@hanabitech.com');
}
